/* global axios */
import ApiClient from './ApiClient';

export const buildContactParams = (page, sortAttr, label, search) => {
  let params = `include_contact_inboxes=false&page=${page}&sort=${sortAttr}`;
  if (search) {
    params = `${params}&q=${search}`;
  }
  if (label) {
    params = `${params}&labels[]=${label}`;
  }
  return params;
};

class ContactAPI extends ApiClient {
  constructor() {
    super('contacts', { accountScoped: true });
  }

  create(data, { helenaKey } = {}) {
    const q = helenaKey ? `?helena_key=${encodeURIComponent(helenaKey)}` : '';
    return axios.post(`${this.url}${q}`, data);
  }

  get(page, sortAttr = 'name', label = '') {
    let requestURL = `${this.url}?${buildContactParams(
      page,
      sortAttr,
      label,
      ''
    )}`;
    return axios.get(requestURL);
  }

  getWithinInbox(inboxId, page, sortAttr = 'name', label = '') {
    let requestURL = `${this.url}?inbox_id=${inboxId}&${buildContactParams(
      page,
      sortAttr,
      label,
      ''
    )}`;
    return axios.get(requestURL);
  }

  getForwardingList(inboxId, page, sortAttr = 'name', label = '') {
    let requestURL = `${this.url}/forward_list?inbox_id=${inboxId}&${buildContactParams(
      page,
      sortAttr,
      label,
      ''
    )}`;
    return axios.get(requestURL);
  }

  getContactsByLastConversation(inboxId, page, sortAttr = 'name', label = '') {
    let requestURL = `${this.url}?inbox_id=${inboxId}&forward=true&${buildContactParams(
      page,
      sortAttr,
      label,
      ''
    )}`;
    return axios.get(requestURL);
  }

  getContactLastConversationByPhoneNumber(phoneNumber) {
    let requestURL = `${this.url}/last_conversation_by_phone_number?phone_number=${phoneNumber.replace('+', '%2B')}`;

    return axios.post(requestURL);
  }

  getConversations(contactId) {
    return axios.get(`${this.url}/${contactId}/conversations`);
  }

  getContactableInboxes(contactId) {
    return axios.get(`${this.url}/${contactId}/contactable_inboxes`);
  }

  getContactLabels(contactId) {
    return axios.get(`${this.url}/${contactId}/labels`);
  }

  updateContactLabels(contactId, labels) {
    return axios.post(`${this.url}/${contactId}/labels`, { labels });
  }

  search(search = '', page = 1, sortAttr = 'name', label = '') {
    let requestURL = `${this.url}/search?${buildContactParams(
      page,
      sortAttr,
      label,
      search
    )}`;

    return axios.post(requestURL);
  }

  // remove helenaKey after migrating back from helena
  searchHelena({
    search = '',
    page = 1,
    sortAttr = 'name',
    label = '',
    helenaKey = '',
  } = {}) {
    let requestURL = `${this.url}/search?${buildContactParams(
      page,
      sortAttr,
      label,
      search
    )}`;
    requestURL = `${requestURL}&helena_key=${helenaKey}`;

    return axios.post(requestURL);
  }

  /**
   * Resolve (find or create) local contact by Helena Hub contact UUID (embed / Messenger / Instagram).
   * @param {string} helenaContactId
   * @param {string} helenaKey
   */
  resolveFromHelena({ helenaContactId, helenaKey = '' } = {}) {
    const k = helenaKey
      ? `&helena_key=${encodeURIComponent(helenaKey)}`
      : '';
    return axios.get(
      `${this.url}/resolve_from_helena?helena_contact_id=${encodeURIComponent(
        helenaContactId
      )}${k}`
    );
  }
  getHelena({ page, sortAttr = 'name', label = '', helenaKey = '' } = {}) {
    let requestURL = `${this.url}?${buildContactParams(
      page,
      sortAttr,
      label,
      ''
    )}`;
    requestURL = `${requestURL}&helena_key=${helenaKey}`;
    return axios.get(requestURL);
  }

  searchWithinInbox(
    inboxId,
    search = '',
    page = 1,
    sortAttr = 'name',
    label = ''
  ) {
    let requestURL = `${this.url}/search?inbox_id=${inboxId}&${buildContactParams(
      page,
      sortAttr,
      label,
      search
    )}`;
    return axios.post(requestURL);
  }

  searchForwardingList(
    inboxId,
    search = '',
    page = 1,
    sortAttr = 'name',
    label = ''
  ) {
    let requestURL = `${this.url}/forward_list?inbox_id=${inboxId}&${buildContactParams(
      page,
      sortAttr,
      label,
      search
    )}`;
    return axios.get(requestURL);
  }

  // eslint-disable-next-line default-param-last
  filter(page = 1, sortAttr = 'name', queryPayload) {
    let requestURL = `${this.url}/filter?${buildContactParams(page, sortAttr)}`;
    return axios.post(requestURL, queryPayload);
  }

  importContacts(file) {
    const formData = new FormData();
    formData.append('import_file', file);
    return axios.post(`${this.url}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  importIclinicContacts(file) {
    const formData = new FormData();
    formData.append('import_file', file);
    return axios.post(`${this.url}/import_iclinic`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  importContactsFromMedx() {
    return axios.post(`${this.url}/import_medx`);
  }

  exportContactsToHelena() {
    return axios.post(`${this.url}/export_helena`);
  }

  destroyCustomAttributes(contactId, customAttributes) {
    return axios.post(`${this.url}/${contactId}/destroy_custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  destroyAvatar(contactId) {
    return axios.delete(`${this.url}/${contactId}/avatar`);
  }

  exportContacts(queryPayload) {
    return axios.post(`${this.url}/export`, queryPayload);
  }

  showHelena(contactId, helenaKey) {
    return axios.get(`${this.url}/${contactId}?helena_key=${helenaKey}`);
  }
}

export default new ContactAPI();
